import { Injectable } from '@angular/core';
import {
	addressString,
	getRegionName,
	getRegionType,
} from '@core/util/address';
import { clientLegalName, isGuarantor } from '@core/util/client';
import { formatCentsToDollars } from '@core/util/money';
import { listParcelsPin } from '@core/util/parcels';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { AddressFormat } from '@models/businessSettings/businessFormatTypes';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import { ClientType } from '@models/clients';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
	MatterFileType,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep, flatMap } from 'lodash';
import { BaseFormMapper } from '../base-form-mapper';
import { joinNames } from '@core/util/string';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { MapAddress } from '@state/global/tags/mappings/tags.address';
import { getLTTLegalDescription } from '@core/util/legalDescription';
import { getBorrowerServiceAddress } from '@core/util/borrowers';

@Injectable({
	providedIn: 'root',
})
export class MLTT_REBATE_FORM_Mapper extends BaseFormMapper<MLTT_REBATE_FORM> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: MLTT_REBATE_FORM,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage?: MatterMortgageWithAssociations,
		businessUsers?: BusinessUser[],
		primaryJurisdiction?: Jurisdiction,
		signors?: PersonMatterClientWithAssociations[],
		otherPartiesWithAssociations?: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterFileType?: MatterFileType,
		addressFormattingSettings?: AddressFormattingSettings,
	) {
		const _formData = formData ? cloneDeep(formData) : ({} as MLTT_REBATE_FORM);

		const FilteredMatterClients =
			matterClients?.filter((client) => !isGuarantor(client)) ?? [];

		const PrimaryClient = FilteredMatterClients?.find(
			(clients) => clients?.primaryContact,
		);

		const ClientsSharesCapacity = clientsCapacity?.shares
			? Object.keys(clientsCapacity?.shares).map((share) => ({
					...clientsCapacity?.shares?.[share],
				}))
			: [];

		const SigningOfficers = flatMap(
			ClientsSharesCapacity.filter((clientShare) => {
				return clientShare?.client?.type === ClientType.CORPORATION;
			}).map((corporation) => {
				return Object.keys(corporation?.client?.associations)
					.filter((key) => {
						return corporation.client.associations[key]?.types?.signing_officer;
					})
					.map((key) => {
						const matterClient = FilteredMatterClients.find(
							(matterClient) => matterClient.id === corporation.client.id,
						);

						return matterClient?.clientAssociations?.find(
							(clientAssociation) => clientAssociation.client.id === key,
						)?.client;
					});
			}),
		);

		const propertyAddress = MapAddress(
			matter?.propertyDetails?.address,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		const legalDescription = getLTTLegalDescription(
			matterTitles,
			matter?.propertyDetails?.propertyType?.type,
			primaryJurisdiction,
			propertyAddress,
		);

		const borrowerServiceAddress = getBorrowerServiceAddress(
			matterFileType,
			signors,
			otherPartiesWithAssociations,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		if (
			(!_formData?.conveyanceOf || _formData?.conveyanceOf?.length === 0) &&
			legalDescription
		) {
			_formData.conveyanceOf = legalDescription;
		}

		if ((!_formData?.pin || _formData?.pin?.length === 0) && matterTitles) {
			_formData.pin = listParcelsPin(matterTitles);
		}

		if (
			(!_formData?.assessmentRollNumber ||
				_formData?.assessmentRollNumber?.length === 0) &&
			matter?.propertyDetails?.rollNumber
		) {
			_formData.assessmentRollNumber = matter?.propertyDetails?.rollNumber;
		}

		if (
			(!_formData?.addressOfLand || _formData?.addressOfLand?.length === 0) &&
			matter?.propertyDetails?.address
		) {
			_formData.addressOfLand = addressString(matter?.propertyDetails?.address);
		}

		if (
			!_formData?.natureOfConveyance ||
			_formData?.natureOfConveyance?.length === 0
		) {
			_formData.natureOfConveyance = 'Transfer of Land';
		}

		if (
			(!_formData?.transferorsName ||
				_formData?.transferorsName?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.transferorsName = joinNames(
				otherParties.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.transfereesName ||
				_formData?.transfereesName?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.transfereesName = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (clientsCapacity?.capacityType && ClientsSharesCapacity) {
			if (ClientsSharesCapacity?.length > 0) {
				if (
					!_formData?.transfereeName1 ||
					_formData?.transfereeName1?.length === 0
				) {
					_formData.transfereeName1 = ClientsSharesCapacity[0]?.name;
				}

				if (
					!_formData?.percentageInterest1 ||
					_formData?.percentageInterest1?.length === 0
				) {
					_formData.percentageInterest1 =
						ClientsSharesCapacity[0]?.share?.toString();
				}
			}

			if (ClientsSharesCapacity?.length > 1) {
				if (
					!_formData?.transfereeName2 ||
					_formData?.transfereeName2?.length === 0
				) {
					_formData.transfereeName2 = ClientsSharesCapacity[1]?.name;
				}

				if (
					!_formData?.percentageInterest2 ||
					_formData?.percentageInterest2?.length === 0
				) {
					_formData.percentageInterest2 =
						ClientsSharesCapacity[1]?.share?.toString();
				}
			}
		}
		if (
			(!_formData?.transfereeName2 ||
				_formData?.transfereeName2?.length === 0) &&
			clientsCapacity?.capacityType &&
			ClientsSharesCapacity?.length > 1
		) {
			_formData.transfereeName2 = ClientsSharesCapacity[0]?.name;
		}

		if (
			(!_formData?.totalConsiderationValue ||
				_formData?.totalConsiderationValue?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.totalConsiderationValue = formatCentsToDollars(
				lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (
			(!_formData?.swornBefore || _formData?.swornBefore?.length === 0) &&
			businessAddress?.city
		) {
			_formData.swornBefore = `City of ${businessAddress?.city}`;
		}

		if (
			(!_formData?.swornBefore2 || _formData?.swornBefore2?.length === 0) &&
			businessAddress?.province
		) {
			_formData.swornBefore2 = `${getRegionType(businessAddress?.province, businessAddress?.country)} of ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (matter?.closingDate) {
			const date = new Date(matter?.closingDate);
			if (!_formData?.month || _formData?.month?.length === 0) {
				_formData.month = format(date, 'MMMM');
			}

			if (!_formData?.day || _formData?.day?.length === 0) {
				_formData.day = format(date, 'dd');
			}

			if (!_formData?.year || _formData?.year?.length === 0) {
				_formData.year = format(date, 'yy');
			}
		}

		if (
			(!_formData?.recipient || _formData?.recipient?.length === 0) &&
			PrimaryClient
		) {
			_formData.recipient = clientLegalName(
				PrimaryClient,
				nameFormattingSettings,
			);
		}

		if (
			(!_formData?.maillingAddress ||
				_formData?.maillingAddress?.length === 0) &&
			borrowerServiceAddress?.lines2?.line1
		) {
			_formData.maillingAddress = borrowerServiceAddress?.lines2?.line1 ?? '';
		}

		if (
			(!_formData?.maillingAddress2 ||
				_formData?.maillingAddress2?.length === 0) &&
			borrowerServiceAddress?.lines2?.line2
		) {
			_formData.maillingAddress2 = borrowerServiceAddress?.lines2?.line2 ?? '';
		}

		if (
			(!_formData?.solicitorName || _formData?.solicitorName?.length === 0) &&
			matter?.lawyerAssigned
		) {
			_formData.solicitorName = matter?.lawyerAssigned?.name;
		}

		if (
			(!_formData?.solicitorAddress ||
				_formData?.solicitorAddress?.length === 0) &&
			businessAddress
		) {
			_formData.solicitorAddress = addressString(businessAddress);
		}

		if (
			(!_formData?.solicitorAddress2 ||
				_formData?.solicitorAddress2?.length === 0) &&
			matter?.number
		) {
			_formData.solicitorAddress2 = `[File ${matter?.number ?? ''}]`;
		}

		if (
			(!_formData?.solicitorTelephoneNumber ||
				_formData?.solicitorTelephoneNumber?.length === 0) &&
			businessAddress?.phoneNumber
		) {
			_formData.solicitorTelephoneNumber = businessAddress?.phoneNumber;
		}

		if (SigningOfficers?.length > 0) {
			if (
				(!_formData?.name1 || _formData?.name1?.length === 0) &&
				SigningOfficers.length >= 1
			) {
				_formData.name1 = clientLegalName(
					SigningOfficers[0],
					nameFormattingSettings,
					{ applyFormatting: false },
				);

				if (
					(!_formData?.date1 || _formData?.date1?.length === 0) &&
					matter?.closingDate
				) {
					_formData.date1 = format(new Date(matter?.closingDate), 'yyyy/MM/');
				}
			}

			if (
				(!_formData?.name2 || _formData?.name2?.length === 0) &&
				SigningOfficers.length >= 2
			) {
				_formData.name2 = clientLegalName(
					SigningOfficers[1],
					nameFormattingSettings,
					{ applyFormatting: false },
				);

				if (
					(!_formData?.date2 || _formData?.date2?.length === 0) &&
					matter?.closingDate
				) {
					_formData.date2 = format(new Date(matter?.closingDate), 'yyyy/MM/');
				}
			}
		}

		return _formData;
	}

	MapValues(): Partial<MLTT_REBATE_FORM> {
		return {};
	}
}

const Mappings: MLTT_REBATE_FORM = {
	conveyanceOf: 'Text1',
	pin: 'Text2',
	assessmentRollNumber: 'Text3',
	addressOfLand: 'Text4',
	instrumentNumber: 'Text5',
	natureOfConveyance: 'CC006',
	transferorsName: 'Text7',
	transfereesName: 'Text8',

	transfereeName1: 'Text9',
	percentageInterest1: 'Text10',
	transfereeName2: 'Text11',
	percentageInterest2: 'Text12',

	makeAndSayCheckbox: 'CC013',
	personAuthorized: 'Text13',

	totalConsiderationValue: 'Text14',

	checkbox2: 'Check Box16',
	checkbox2Date: 'Text15',
	checkbox2Date2: 'Text16',
	checkbox2Date3: 'Text17',
	checkbox2Date4: 'Text18',

	nameOfSpouse: 'Text19',
	nameOfPurchaser: 'Text20',
	spouseSignature: 'Text21',
	spouseDeclarationDate: 'Text20sdfsdfsdfd',

	swornBefore: 'Text23',
	swornBefore2: 'Text24',

	printName1: 'Text28',
	printName2: 'Text29',

	commissioner: 'Text28sdfsfsd',

	day: 'Text25',
	month: 'Text26',
	year: 'Text27',

	recipient: 'Text30',
	solicitorName: 'Text31',

	maillingAddress: 'Text32',
	maillingAddress2: 'Text33',

	solicitorAddress: 'Text34',
	solicitorAddress2: 'Text35',

	telephoneNumber: 'Text36',
	solicitorTelephoneNumber: 'Text37',

	name1: 'Text38',
	name2: 'Text39',

	date1: 'cc022',
	date2: 'CC043',
};

export interface MLTT_REBATE_FORM {
	conveyanceOf: string;
	pin: string;
	assessmentRollNumber: string;
	addressOfLand: string;
	instrumentNumber: string;
	natureOfConveyance: string;
	transferorsName: string;
	transfereesName: string;

	transfereeName1: string;
	percentageInterest1: string;
	transfereeName2: string;
	percentageInterest2: string;

	makeAndSayCheckbox: string;
	personAuthorized: string;

	totalConsiderationValue: string;

	checkbox2: string;

	checkbox2Date: string;
	checkbox2Date2: string;
	checkbox2Date3: string;
	checkbox2Date4: string;

	nameOfSpouse: string;
	nameOfPurchaser: string;

	spouseSignature: string;
	spouseDeclarationDate: string;

	swornBefore: string;
	swornBefore2: string;

	printName1: string;
	printName2: string;

	commissioner: string;

	day: string;
	month: string;
	year: string;

	recipient: string;
	solicitorName: string;

	maillingAddress: string;
	maillingAddress2: string;

	solicitorAddress: string;
	solicitorAddress2: string;

	telephoneNumber: string;

	solicitorTelephoneNumber: string;

	name1: string;
	name2: string;
	date1: string;
	date2: string;
}
